import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShippingOutlined";
import { Alert, Card, CardContent, Typography } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Base } from "../../framework/common/base";
import { LinkButton } from "../../framework/common/linkButton";
import { useInterval } from "../../hooks/framework/useInterval";
import { useSelectCurrentWorkShift } from "../../hooks/worktime/useSelectWorkShifts";
import { useSelectWorkShiftTimeSlotByIds } from "../../hooks/worktime/useSelectWorkShiftTimeSlots";
import { useIsWaitingForPlannedShift } from "../../hooks/worktime/useSelectWorkShifts";
import { AppUtils } from "../../models/common/appUtils";
import { getWorkShiftTimeSlotDurationMinutes, IWorkShiftTimeSlot } from "../../models/workShiftTimeSlot/workShiftTimeSlot";
import { CostCenterInfo } from "./costCenterInfo";

interface IWorkTimeCurrentDetailsProps {
    current: IWorkShiftTimeSlot;
}

export const WorkTimeCurrentDetails = ({
    current,
}: IWorkTimeCurrentDetailsProps) => {
    const [workHours, setWorkHours] = useState<number>();
    const { t } = useTranslation();
    const shift = useSelectCurrentWorkShift();
    const shiftSlots = useSelectWorkShiftTimeSlotByIds(shift?.workShiftTimeSlotIds);

    const updateWorkHours = useCallback(
        () =>
            setWorkHours(
                shiftSlots
                    ?.map((i) => getWorkShiftTimeSlotDurationMinutes(i))
                    .reduce((acc, cur) => acc + cur, 0)
            ),
        [shiftSlots]
    );

    useEffect(() => {
        updateWorkHours();
    }, [updateWorkHours]);

    useInterval(updateWorkHours, shiftSlots?.length ? 30000 : null);

    const isWaitingForPlannedShift = useIsWaitingForPlannedShift();

    return (
        <Card sx={{ backgroundColor: "gray.light" }}>
            <CardContent>
                {isWaitingForPlannedShift && (
                    <Alert severity="info" sx={{ mb: 2, color: "orange" }}>
                        {t("workTime.recordingInProgress")}
                    </Alert>
                )}
                <Grid2 container rowSpacing={2}>
                    <Grid2 container xs={12} alignItems="center">
                        <Grid2 xs={7}>
                            <Typography variant="h5">
                                {t("workTime.customer")}
                            </Typography>
                        </Grid2>
                        <Grid2
                            container
                            xs={5}
                            justifyContent="right"
                            alignItems="center"
                        >
                            <Grid2 display={"flex"} alignItems={"center"} paddingRight={"5px"}>
                                <LinkButton
                                    sx={{
                                        padding: "0px",
                                        margin: "0px",
                                        minWidth: "0px",
                                    }}
                                    to="/worklog/calendar"
                                >
                                    <CalendarTodayIcon />
                                </LinkButton>
                            </Grid2>
                            <Grid2>
                                <Typography variant="h5">
                                    {Base.dateToDateStr(new Date())}
                                </Typography>
                            </Grid2>
                        </Grid2>
                    </Grid2>
                    <Grid2 xs={7}>
                        <Typography
                            variant="h4"
                            sx={
                                !current?.customerName
                                    ? { color: "gray.main" }
                                    : undefined
                            }
                        >
                            {current?.customerName ??
                                t("workTime.noCustomerSelected")}
                        </Typography>
                    </Grid2>
                    <Grid2 xs={7}>
                        <Typography variant="h5">
                            {t("workTime.costCenter")}
                        </Typography>
                    </Grid2>
                    <Grid2 container xs={12} alignItems={"center"} mb={1}>
                        <Grid2 xs={7}>
                            <CostCenterInfo slot={current} />
                        </Grid2>
                        <Grid2 xs={5} textAlign="right">
                            <LinkButton
                                variant="contained"
                                sx={{
                                    alignItems: "left",
                                    height: 32,
                                    borderRadius: 10,
                                }}
                                to="/work/start"
                            >
                                <LocalShippingIcon
                                    fontSize={"large"}
                                    sx={{ pr: 1 }}
                                />
                                <Typography
                                    variant="h4"
                                    sx={{
                                        color: "inherit",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {current?.vehicleRegisterNumber || "----"}
                                </Typography>
                            </LinkButton>
                        </Grid2>
                    </Grid2>
                    <Grid2
                        container
                        xs={12}
                        sx={{
                            justifyContent: "space-between",
                            borderTop: 1,
                            borderColor: "gray.main",
                            pt: 1,
                        }}
                    >
                        <Grid2 xs={2} display="flex" alignItems="center">
                            <AccessTimeIcon fontSize="large" />
                        </Grid2>
                        <Grid2 xs={6} alignItems="center">
                            <Typography>
                                {t("workTime.workShiftStart")}
                            </Typography>
                            <Typography variant="h4">
                                {shift
                                    ? `${Base.dateStrToOriginalTimezoneDateStrShort(
                                          shift.startTime
                                      )} ${t(
                                          "time.at"
                                      )} ${Base.dateStrToOriginalTimezoneTimeStr(
                                          shift.startTime
                                      )} ${Base.dateTzOffsetIfNotLocal(
                                          shift.startTime,
                                          shift.startTimeZoneName
                                      )}`
                                    : "----"}
                            </Typography>
                        </Grid2>
                        <Grid2 xs={4} alignItems="center">
                            <Typography>{t("workTime.workTime")}</Typography>
                            <Typography variant="h4">
                                {shift
                                    ? shift?.startTimePlannedWorkShiftId
                                        ? AppUtils.getDurationStrByDurationMin(
                                            Base.dateDiffInMinutes(shift.startTime, new Date()),
                                            true
                                        )
                                        : AppUtils.getDurationStrByDurationMin(workHours, true)
                                    : "----"}
                            </Typography>
                        </Grid2>
                    </Grid2>
                </Grid2>
            </CardContent>
        </Card>
    );
};
